import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { festeZahlen, formelGeruest, hatAbsolutenBezug } from "@/office/excel/formeln";
import { formulaCells, readWorkbook, type CellInfo } from "@/office/excel/workbook";
import { KEIN_INHALT, nichtPruefbar } from "../schnittstelle";

/**
 * Findet Spalten, in denen dieselbe Formel über mehrere Zeilen ausgefüllt wurde.
 *
 * Erkennungsmerkmal ist das Formelgerüst: Alle Zellen einer ausgefüllten Spalte
 * tragen dasselbe, obwohl ihre Bezüge auf verschiedene Zeilen zeigen.
 */
function ausgefuellteSpalten(formeln: CellInfo[]): Array<{ geruest: string; zellen: CellInfo[] }> {
  const gruppen = new Map<string, CellInfo[]>();
  for (const zelle of formeln) {
    const schluessel = `${zelle.column}|${formelGeruest(zelle.formula)}`;
    const vorhandene = gruppen.get(schluessel) ?? [];
    vorhandene.push(zelle);
    gruppen.set(schluessel, vorhandene);
  }
  return [...gruppen.entries()]
    .filter(([, zellen]) => zellen.length >= 3)
    .map(([schluessel, zellen]) => ({ geruest: schluessel.split("|")[1], zellen }));
}

export const lektionBezuege: Lesson = {
  id: "bezuege",
  title: "Bezüge statt getippter Zahlen",
  summary: "Der zweite rote Faden: Was sich ändern kann, gehört in eine eigene Zelle – nicht in die Formel.",
  durationMinutes: 13,
  learningGoals: [
    "Veränderliche Werte in eigene Zellen auslagern",
    "Formeln nach unten ausfüllen statt sie einzeln zu tippen",
    "Bezüge mit F4 festhalten und den Unterschied erklären",
  ],
  steps: [
    {
      id: "keine-konstanten",
      title: "Die 19 gehört nicht in die Formel",
      explanation: [
        "=B7*1,19 rechnet richtig. Und ist trotzdem der Fehler, der Tabellen kaputt " +
          "macht. Denn der Mehrwertsteuersatz steht jetzt in der Formel – und wenn er " +
          "sich ändert, muss jede einzelne Formel angefasst werden. In einer Tabelle " +
          "mit dreihundert Zeilen findet man dabei zuverlässig nicht alle.",
        "Schlimmer noch: Man sieht ihn nicht. Eine Tabelle zeigt Ergebnisse, keine " +
          "Formeln. Dass irgendwo in Zeile 147 ein anderer Satz steckt, fällt " +
          "frühestens bei der Prüfung auf. Wer eine fremde Tabelle übernimmt, verbringt " +
          "die erste Stunde damit, solche Stellen zu suchen.",
        "Die Regel dagegen ist knapp: Was sich ändern kann, bekommt eine eigene Zelle. " +
          "Steuersatz, Stundensatz, Rabattstufe, Umrechnungskurs – jeweils eine Zelle, " +
          "sinnvollerweise auf einem eigenen Blatt „Einstellungen“, jede mit einer " +
          "Beschriftung daneben. Die Formel heißt dann =B7*(1+Einstellungen!B1), und " +
          "eine Änderung wirkt überall auf einmal.",
        "Nicht gemeint sind Zahlen, die zur Bedienung einer Funktion gehören: die 2 in " +
          "=RUNDEN(A1;2) ist die Anzahl der Nachkommastellen, die 3 in einem SVERWEIS " +
          "die Spaltennummer. Solche Zahlen beschreiben nicht die Sache, sondern die " +
          "Rechenanweisung – sie dürfen bleiben.",
      ],
      keyPoints: [
        "Was sich ändern kann, bekommt eine eigene Zelle mit Beschriftung.",
        "Ein eigenes Blatt „Einstellungen“ hält alle Stellschrauben an einem Ort.",
        "Argumente von Funktionen sind keine versteckten Konstanten.",
      ],
      task: {
        instruction:
          "Sieh deine Formeln durch: Keine soll eine feste Zahl enthalten, die " +
          "eigentlich in eine eigene Zelle gehört. Lagere solche Werte aus und verweise " +
          "darauf.",
        hint: "Steuersatz, Stundensatz, Rabatt: in eine eigene Zelle schreiben, in der Formel darauf zeigen.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Die Formeln der Zellen",
              "1.4",
              "Drücke Strg + # – Excel zeigt dann alle Formeln statt der Ergebnisse an. So findest du getippte Zahlen schnell.",
              true,
            );
          }

          const formeln = formulaCells(mappe);
          if (formeln.length === 0) {
            return fail(
              "Es steht noch keine Formel in der Arbeitsmappe.",
              "Löse zuerst die Aufgabe aus der vorigen Lektion.",
            );
          }

          const mitKonstante = formeln
            .map((zelle) => ({ zelle, zahlen: festeZahlen(zelle.formula) }))
            .filter((eintrag) => eintrag.zahlen.length > 0);

          if (mitKonstante.length === 0) {
            return pass(
              `Alle ${formeln.length} Formeln rechnen mit Bezügen statt mit getippten Zahlen. ` +
                "Ändert sich ein Wert, ändert sich genau eine Zelle.",
            );
          }

          const erster = mitKonstante[0];
          return fail(
            `In Zelle ${erster.zelle.address} steht die ${erster.zahlen[0]} direkt in der Formel ` +
              `(„${erster.zelle.formula}“) statt eines Bezugs auf eine eigene Zelle.` +
              (mitKonstante.length > 1
                ? ` Insgesamt betrifft das ${mitKonstante.length} Formeln, unter anderem auch ` +
                  `${mitKonstante.slice(1, 3).map((eintrag) => eintrag.zelle.address).join(" und ")}.`
                : ""),
            `Schreibe die ${erster.zahlen[0]} in eine eigene Zelle mit Beschriftung daneben und ` +
              "verweise in der Formel darauf. Argumente von Funktionen – etwa die Stellenzahl bei " +
              "RUNDEN – sind davon ausgenommen und werden hier nicht bemängelt.",
          );
        },
      },
    },
    {
      id: "ausfuellen",
      title: "Einmal schreiben, nach unten ziehen",
      explanation: [
        "Eine Formel wird in Excel genau einmal geschrieben. Danach wird sie " +
          "ausgefüllt: Am rechten unteren Eck der markierten Zelle sitzt ein kleines " +
          "Quadrat, das Ausfüllkästchen. Ziehst du es nach unten, überträgt Excel die " +
          "Formel Zeile für Zeile und passt die Bezüge automatisch an. Ein Doppelklick " +
          "auf das Kästchen füllt in einem Rutsch bis zum Ende der Nachbarspalte aus.",
        "Das ist nicht nur bequem, es ist auch die Absicherung gegen den schwersten " +
          "Fehlertyp in Tabellen: die einzelne abweichende Formel. Wer jede Zeile " +
          "einzeln tippt, produziert früher oder später eine, die auf die falsche Zeile " +
          "zeigt – und weil das Ergebnis plausibel aussieht, fällt es niemandem auf.",
        "Ausgefüllt wird auch quer: Dieselbe Bewegung nach rechts überträgt eine Formel " +
          "über Spalten hinweg. Und Strg + U füllt die markierte Zelle mit dem Inhalt " +
          "der Zelle darüber – nützlich, wenn nur eine einzelne Lücke zu schließen ist.",
      ],
      keyPoints: [
        "Ausfüllkästchen: das kleine Quadrat rechts unten an der Markierung.",
        "Doppelklick darauf füllt bis zum Ende der Nachbarspalte aus.",
        "Strg + U übernimmt die Formel aus der Zelle darüber.",
      ],
      task: {
        instruction:
          "Fülle eine deiner Formeln über mindestens drei Zeilen aus, sodass in der " +
          "ganzen Spalte dieselbe Rechnung steht.",
        hint: "Formelzelle markieren, das Ausfüllkästchen rechts unten packen und nach unten ziehen – oder doppelklicken.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Die Formeln der Zellen",
              "1.4",
              "Drücke Strg + # und sieh nach, ob in der ganzen Spalte dieselbe Rechnung steht.",
              true,
            );
          }

          const formeln = formulaCells(mappe);
          if (formeln.length === 0) {
            return fail(
              "Es steht noch keine Formel in der Arbeitsmappe.",
              "Schreibe zuerst eine Formel, die mit zwei Zellen deiner Liste rechnet.",
            );
          }

          const spalten = ausgefuellteSpalten(formeln);
          if (spalten.length === 0) {
            return fail(
              `Es gibt ${formeln.length} ${formeln.length === 1 ? "Formel" : "Formeln"}, aber keine, ` +
                "die über mindestens drei Zeilen derselben Spalte durchgezogen ist.",
              "Markiere deine Formelzelle und ziehe das Ausfüllkästchen nach unten. Excel passt die " +
                "Zeilennummern dabei selbst an.",
            );
          }

          const groesste = spalten.sort((a, b) => b.zellen.length - a.zellen.length)[0];
          const erste = groesste.zellen[0];
          const letzte = groesste.zellen[groesste.zellen.length - 1];
          return pass(
            `In ${erste.address} bis ${letzte.address} steht dieselbe Rechnung – ` +
              `${groesste.zellen.length} Zellen mit angepassten Bezügen, keine Abweichung dazwischen.`,
          );
        },
      },
    },
    {
      id: "absolut",
      title: "Mit F4 einen Bezug festhalten",
      explanation: [
        "Beim Ausfüllen verschieben sich Bezüge mit – das ist meist genau richtig und " +
          "manchmal genau falsch. Zeigt die Formel auf den Steuersatz in B1, soll dieser " +
          "Bezug beim Ausfüllen eben nicht auf B2, B3, B4 weiterwandern. Er muss " +
          "stehenbleiben.",
        "Dafür gibt es das Dollarzeichen. $B$1 hält Spalte und Zeile fest, der Bezug " +
          "zeigt nach dem Ausfüllen immer noch auf B1. Man nennt das einen absoluten " +
          "Bezug; ohne Dollarzeichen heißt er relativ. Getippt werden die Dollarzeichen " +
          "praktisch nie – die Taste F4 setzt sie, während die Formel offen ist und der " +
          "Cursor am Bezug steht.",
        "F4 schaltet dabei durch vier Zustände: B1 → $B$1 → B$1 → $B1 → B1. Die " +
          "mittleren beiden sind gemischte Bezüge, die nur die Zeile oder nur die " +
          "Spalte festhalten. Sie sind das Werkzeug für Kreuztabellen: Eine einzige " +
          "Formel, in beide Richtungen ausgefüllt, füllt das ganze Raster – etwa eine " +
          "Preistabelle mit Mengen in der Kopfzeile und Rabattstufen in der ersten " +
          "Spalte.",
        "Ein untrügliches Zeichen für einen vergessenen absoluten Bezug: Die erste " +
          "Zeile stimmt, alle darunter liefern Unsinn oder Null. Dann zeigt der Bezug " +
          "auf eine leere Zelle unterhalb des eigentlichen Ziels.",
      ],
      keyPoints: [
        "F4 setzt die Dollarzeichen, während der Cursor in der Formel am Bezug steht.",
        "$B$1 bleibt beim Ausfüllen stehen, B1 wandert mit.",
        "Erste Zeile richtig, alle darunter null: ein vergessenes Dollarzeichen.",
      ],
      task: {
        instruction:
          "Baue eine Formel, die auf eine feste Parameterzelle verweist – etwa einen " +
          "Steuersatz oder Rabatt – und halte diesen Bezug mit F4 fest. Fülle die Formel " +
          "danach nach unten aus.",
        hint: "In der Formel den Cursor an den Bezug setzen und F4 drücken. Aus B1 wird $B$1.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Die Formeln der Zellen",
              "1.4",
              "Sieh in der Bearbeitungsleiste nach, ob vor Spaltenbuchstabe und Zeilennummer je ein Dollarzeichen steht.",
              true,
            );
          }

          const formeln = formulaCells(mappe);
          if (formeln.length === 0) {
            return fail(KEIN_INHALT, "Schreibe zuerst eine Formel.");
          }

          const absolut = formeln.filter((zelle) => hatAbsolutenBezug(zelle.formula));
          if (absolut.length === 0) {
            return fail(
              `Keine der ${formeln.length} Formeln enthält einen festgehaltenen Bezug.`,
              "Setze in einer Formel den Cursor auf den Bezug zur Parameterzelle und drücke F4. " +
                "Aus B1 wird dann $B$1.",
            );
          }

          // Der Nutzen zeigt sich erst beim Ausfüllen: Sind mehrere Zellen mit
          // demselben absoluten Bezug vorhanden?
          const ausgefuellt = ausgefuellteSpalten(absolut);
          const erste = absolut[0];
          if (ausgefuellt.length === 0) {
            return pass(
              `In ${erste.address} steht „${erste.formula}“ mit einem festgehaltenen Bezug. ` +
                "Ziehe die Formel noch nach unten – erst dann zeigt sich, wozu die Dollarzeichen gut sind.",
            );
          }

          const gruppe = ausgefuellt[0];
          return pass(
            `„${erste.formula}“ hält den Bezug fest und ist über ${gruppe.zellen.length} Zeilen ` +
              "ausgefüllt – der Parameter bleibt in jeder Zeile derselbe.",
          );
        },
      },
    },
  ],
};
